<template>
  <div>
    <div class="form__success" v-if="form.submitStatus === 'OK'">
      <h1>
        <i class="fal fa-check-circle"></i>
      </h1>
      <h2>Product Updated</h2>
    </div>
    <div class="form__error" v-if="form.submitStatus === 'SERVER_ERROR'">
      <h1>
        <i class="fal fa-times-circle"></i>
      </h1>
      <h2>Error Updating Product</h2>
    </div>
    <form
      class="form"
      @submit.prevent="submit"
      v-if="form.submitStatus !== 'OK' && form.submitStatus !== 'SERVER_ERROR'"
    >
      <Field label="Category" type="black">
        <AutoCompleteInput
          v-model.trim="$v.form.categoryId.$model"
          :items="categories"
          placeholder="Category"
          @input="updateTraits"
          v-bind:hasError="
            form.submitStatus === 'ERROR' && !$v.form.categoryId.required
          "
          errorMsg="This field is required"
          :canAdd="false"
        />
      </Field>

      <Field label="Brand" type="black">
        <AutoCompleteInput
          v-model.trim="$v.form.brand.$model"
          :items="productBrands($v.form.categoryId.$model)"
          placeholder="Brand"
          @input="updateShortUrl"
          v-bind:hasError="
            form.submitStatus === 'ERROR' && !$v.form.brand.required
          "
          errorMsg="This field is required"
        />
      </Field>

      <Field label="Name" type="black">
        <AutoCompleteInput
          v-model.trim="$v.form.name.$model"
          :items="productNames($v.form.categoryId.$model)"
          placeholder="Product name"
          @input="updateShortUrl"
          v-bind:hasError="
            form.submitStatus === 'ERROR' && !$v.form.name.required
          "
          errorMsg="This field is required"
        />
      </Field>

      <Field label="Edition" type="black">
        <AutoCompleteInput
          v-model.trim="$v.form.edition.$model"
          :items="productEditions($v.form.name.$model)"
          placeholder="Product edition"
          @input="updateShortUrl"
          v-bind:hasError="
            form.submitStatus === 'ERROR' && !$v.form.edition.required
          "
          errorMsg="This field is required"
        />
      </Field>

      <Field label="Image" type="black">
        <FileInput v-model.trim="$v.form.image.$model" />
      </Field>

      <div v-if="category">
        <Field
          v-for="trait in category.traits"
          :key="trait"
          :label="trait"
          type="black"
        >
          <AutoCompleteInput
            v-model.trim="$v.form[trait].$model"
            :items="getProductCategoryOptions(category.id, trait)"
            :placeholder="trait"
            @input="updateShortUrl"
            v-bind:hasError="
              form.submitStatus === 'ERROR' && !$v.form.name.required
            "
            errorMsg="This field is required"
          />
        </Field>
      </div>

      <Field label="Short Url" type="black">
        <TextInput
          v-model.trim="$v.form.shorturl.$model"
          placeholder="brand-name-edition-colour"
        />
      </Field>

      <Field>
        <ButtonSubmit type="secondary">Submit</ButtonSubmit>
      </Field>
    </form>
  </div>
</template>

<script>
import { required, requiredIf, email } from 'vuelidate/lib/validators';
import { mapState, mapGetters, mapActions } from 'vuex';

export default {
  props: {
    productId: String,
    mode: String,
  },
  data: () => {
    return {
      id: null,
      category: null,
      form: {
        categoryId: null,
        brand: null,
        name: '',
        edition: null,
        image: null,
        shorturl: '',
        submitStatus: null,
      },
    };
  },
  validations() {
    let validations = {
      form: {
        categoryId: {
          required,
        },
        brand: {
          required,
        },
        name: {
          required,
        },
        edition: {},
        shorturl: {
          required,
        },
        image: {},
      },
    };

    if (this.category) {
      this.category.traits.forEach((trait) => (validations.form[trait] = {}));
    }

    return validations;
  },
  computed: {
    ...mapGetters({
      categories: 'category/getCategoryOptions',
      getProductCategoryOptions: 'product/getCategories',
      getCategory: 'category/getCategory',
      getProduct: 'product/getProduct',
      productBrands: 'product/getBrandOptions',
      productNames: 'product/getNameOptions',
      productEditions: 'product/getEditionOptions',
    }),
  },
  methods: {
    ...mapActions({
      addProduct: 'product/addProduct',
      editProduct: 'product/editProduct',
    }),
    updateTraits() {
      this.category = this.getCategory(this.form.categoryId);
    },
    updateShortUrl() {
      if (!this.form.brand || !this.form.name) {
        return;
      }

      this.form.shorturl = `${this.form.brand.replace(
        ' ',
        '-'
      )}-${this.form.name.replace(' ', '-')}`.toLowerCase();
    },
    async submit() {
      this.$v.$touch();
      if (this.$v.$invalid) {
        this.form.submitStatus = 'ERROR';
      } else {
        this.$emit('onSubmit');

        try {
          switch (this.mode) {
            case 'edit':
              console.log('edit', this.form);
              await this.editProduct(this.form);
              break;
            case 'duplicate':
              delete this.form.id;
              console.log('duplicate', this.form);
              await this.addProduct(this.form);
              break;
            default:
              console.log('add', this.form);
              await this.addProduct(this.form);
              break;
          }
          this.form.submitStatus = 'OK';
        } catch (error) {
          this.form.submitStatus = 'SERVER_ERROR';
        }
      }
    },
  },
  watch: {
    productId: {
      immediate: true,
      handler(value) {
        this.id = value;
        if (this.id) {
          this.product = this.getProduct(this.id);
          this.form.id = this.product.id;
          this.form.categoryId = this.product.categoryId;
          this.form.brand = this.product.brand;
          this.form.name = this.product.name;
          this.form.edition = this.product.edition;
          this.form.image = this.product.image;
          this.form.shorturl = this.product.shorturl;

          if (this.product.categoryId) {
            this.updateTraits();
            this.category.traits.forEach(
              (trait) => (this.form[trait] = this.product[trait])
            );
          }
        }
      },
    },
  },
};
</script>

<style lang="scss" scoped>
.form__success,
.form__error {
  padding: $padding 0;
  text-align: center;

  h1 {
    color: $secondary;
    font-size: 4.8rem;
  }

  h2 {
    color: $primary;
    font-size: 2.4rem;
    font-weight: 400;
  }
}

.form__error {
  h1,
  h2 {
    color: $error;
  }
}
</style>
